import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-hot-toast";
import { 
  FiPlus, 
  FiEdit2, 
  FiTrash2, 
  FiLoader, 
  FiEye,
  FiSave,
  FiX,
  FiFileText,
  FiChevronLeft,
  FiChevronRight,
  FiCalendar
} from "react-icons/fi";

const CompanyAbout = () => {
  const [loading, setLoading] = useState(true);
  const [aboutUsList, setAboutUsList] = useState([]);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedAbout, setSelectedAbout] = useState(null);
  
  // Form states
  const [newContent, setNewContent] = useState("");
  const [editingContent, setEditingContent] = useState({ id: null, content: "" });
  
  // Loading states
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const API_URL = "https://api.dreamtoday.in/api/admin";

  // Fetch all about us content
  const fetchAboutUs = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/allabout-us`, {
        withCredentials: true
      });
      
      console.log("About Us response:", response.data);
      
      if (response.data.success) {
        setAboutUsList(response.data.abouts || []);
      }
    } catch (error) {
      console.error("Error fetching about us:", error);
      toast.error("Failed to fetch about us content");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAboutUs();
  }, []);

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = aboutUsList.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(aboutUsList.length / itemsPerPage);

  // Handle create/upsert about us
  const handleCreateAbout = async (e) => {
    e.preventDefault();
    setCreating(true);

    try {
      if (!newContent.trim()) {
        toast.error("Content is required");
        setCreating(false);
        return;
      }

      const response = await axios.post(
        `${API_URL}/createabout-us`,
        { content: newContent },
        { withCredentials: true }
      );

      if (response.data.success) {
        toast.success("About Us content saved successfully!");
        setShowCreateModal(false);
        setNewContent("");
        fetchAboutUs();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to save content");
    } finally {
      setCreating(false);
    }
  };

  // Handle update about us
  const handleUpdateAbout = async (e) => {
    e.preventDefault();
    setUpdating(true);

    try {
      if (!editingContent.content.trim()) {
        toast.error("Content is required");
        setUpdating(false);
        return;
      }

      const response = await axios.put(
        `${API_URL}/updateabout-us/${editingContent.id}`,
        { content: editingContent.content },
        { withCredentials: true }
      );

      if (response.data.success) {
        toast.success("About Us content updated successfully!");
        setShowEditModal(false);
        setEditingContent({ id: null, content: "" });
        fetchAboutUs();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update content");
    } finally {
      setUpdating(false);
    }
  };

  // Handle delete about us
  const handleDeleteAbout = async () => {
    if (!selectedAbout) return;
    
    setDeleting(true);
    try {
      const response = await axios.delete(
        `${API_URL}/deleteabout-us/${selectedAbout._id}`,
        { withCredentials: true }
      );

      if (response.data.success) {
        toast.success("About Us content deleted successfully!");
        setShowDeleteModal(false);
        setSelectedAbout(null);
        fetchAboutUs();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete content");
    } finally {
      setDeleting(false);
    }
  };

  // Format date
  const formatDate = (date) => new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  // Truncate content for display
  const truncateContent = (content, length = 100) => {
    if (content.length <= length) return content;
    return content.substring(0, length) + '...';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <FiLoader className="animate-spin text-4xl text-blue-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
              <FiFileText className="text-blue-500" />
              Company About Us
            </h1>
            <p className="text-gray-600 mt-1">Total {aboutUsList.length} about entries found</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <FiPlus /> Add About Us
          </button>
        </div>

        {/* Table */}
        {aboutUsList.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <FiFileText className="text-6xl text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No About Us Content Found</h3>
            <p className="text-gray-500 mb-6">Get started by adding your company's about us information</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Add About Us
            </button>
          </div>
        ) : (
          <>
            <div className="bg-white rounded-lg shadow overflow-hidden mb-6">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold">S.No</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Content</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Created At</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {currentItems.map((about, idx) => (
                      <tr key={about._id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {indexOfFirstItem + idx + 1}
                        </td>
                        <td className="px-4 py-3 max-w-md">
                          <div className="flex items-start gap-2">
                            <FiFileText className="text-blue-600 mt-1 flex-shrink-0" />
                            <span className="text-sm text-gray-700">
                              {truncateContent(about.content, 150)}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <FiCalendar className="text-green-600" />
                            <span className="text-sm">{formatDate(about.createdAt)}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-2">
                            <button
                              onClick={() => {
                                setSelectedAbout(about);
                                setShowViewModal(true);
                              }}
                              className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                              title="View"
                            >
                              <FiEye />
                            </button>
                            <button
                              onClick={() => {
                                setEditingContent({
                                  id: about._id,
                                  content: about.content
                                });
                                setShowEditModal(true);
                              }}
                              className="p-1.5 text-green-600 hover:bg-green-50 rounded"
                              title="Edit"
                            >
                              <FiEdit2 />
                            </button>
                            <button
                              onClick={() => {
                                setSelectedAbout(about);
                                setShowDeleteModal(true);
                              }}
                              className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                              title="Delete"
                            >
                              <FiTrash2 />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-between items-center">
                <div className="text-sm text-gray-600">
                  Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, aboutUsList.length)} of {aboutUsList.length}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
                    disabled={currentPage === 1}
                    className="p-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50"
                  >
                    <FiChevronLeft />
                  </button>
                  <span className="px-4 py-2 bg-blue-600 text-white rounded-lg">
                    {currentPage} / {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="p-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50"
                  >
                    <FiChevronRight />
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {/* Create/Upsert Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-2xl w-full">
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-bold">Add About Us Content</h2>
                  <button onClick={() => setShowCreateModal(false)} className="p-1 hover:bg-gray-100 rounded">
                    <FiX />
                  </button>
                </div>

                <form onSubmit={handleCreateAbout} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1 flex items-center gap-2">
                      <FiFileText className="text-blue-600" /> Content *
                    </label>
                    <textarea
                      value={newContent}
                      onChange={(e) => setNewContent(e.target.value)}
                      rows="10"
                      className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                      placeholder="Write about your company, mission, vision, values, etc..."
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Note: Creating new content will replace any existing about us information.
                    </p>
                  </div>

                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setShowCreateModal(false)}
                      className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={creating}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                    >
                      {creating ? <FiLoader className="animate-spin" /> : <FiSave />}
                      {creating ? "Saving..." : "Save"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Edit Modal */}
        {showEditModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-2xl w-full">
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-bold">Edit About Us Content</h2>
                  <button onClick={() => setShowEditModal(false)} className="p-1 hover:bg-gray-100 rounded">
                    <FiX />
                  </button>
                </div>

                <form onSubmit={handleUpdateAbout} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1 flex items-center gap-2">
                      <FiFileText className="text-blue-600" /> Content *
                    </label>
                    <textarea
                      value={editingContent.content}
                      onChange={(e) => setEditingContent({...editingContent, content: e.target.value})}
                      rows="10"
                      className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                      required
                    />
                  </div>

                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setShowEditModal(false)}
                      className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={updating}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
                    >
                      {updating ? <FiLoader className="animate-spin" /> : <FiSave />}
                      {updating ? "Updating..." : "Update"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* View Modal */}
        {showViewModal && selectedAbout && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-2xl w-full">
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-bold">About Us Details</h2>
                  <button onClick={() => setShowViewModal(false)} className="p-1 hover:bg-gray-100 rounded">
                    <FiX />
                  </button>
                </div>

                <div className="space-y-4">
                  <div className="bg-gray-50 p-3 rounded">
                    <p className="text-xs text-gray-500 mb-1">Content ID</p>
                    <p className="text-sm font-mono break-all">{selectedAbout._id}</p>
                  </div>

                  <div className="bg-gray-50 p-3 rounded">
                    <p className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                      <FiFileText className="text-blue-600" /> Content
                    </p>
                    <div className="text-sm whitespace-pre-wrap bg-white p-3 rounded border max-h-96 overflow-y-auto">
                      {selectedAbout.content}
                    </div>
                  </div>

                  <div className="bg-gray-50 p-3 rounded">
                    <p className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                      <FiCalendar className="text-green-600" /> Created At
                    </p>
                    <p className="text-sm">{formatDate(selectedAbout.createdAt)}</p>
                  </div>
                </div>

                <div className="flex justify-end gap-2 mt-6">
                  <button
                    onClick={() => {
                      setShowViewModal(false);
                      setEditingContent({
                        id: selectedAbout._id,
                        content: selectedAbout.content
                      });
                      setShowEditModal(true);
                    }}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => setShowViewModal(false)}
                    className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete Modal */}
        {showDeleteModal && selectedAbout && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-md w-full p-6">
              <div className="text-center">
                <FiTrash2 className="text-5xl text-red-500 mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-2">Delete About Us Content</h3>
                <p className="text-gray-600 mb-6">
                  Are you sure you want to delete this about us content? This action cannot be undone.
                </p>
                
                <div className="flex justify-center gap-3">
                  <button
                    onClick={() => setShowDeleteModal(false)}
                    className="px-6 py-2 border rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeleteAbout}
                    disabled={deleting}
                    className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
                  >
                    {deleting ? <FiLoader className="animate-spin" /> : <FiTrash2 />}
                    {deleting ? "Deleting..." : "Delete"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CompanyAbout;